if (!customElements.get('td-notifications')) {
  class TdNotifications extends HTMLElement {
    static SEEN_STORAGE_KEY = 'td-notifications-seen';
    static SUBMITTED_STORAGE_KEY = 'td-notifications-submitted';
    static DESKTOP_QUERY = '(min-width: 750px)';
    static HEADER_HIDDEN_CLASS = 'shopify-section-header-hidden';
    static OPEN_CLASS = 'td-notifications__panel--open';
    static SCROLL_LOCK_CLASS = 'overflow-hidden-mobile';
    // Lifts .section-header above the footer's localization stack while the panel is open.
    static BODY_OPEN_CLASS = 'td-notifications-open';

    // Safari private mode and hardened privacy settings throw on storage access,
    // so every read/write is guarded and degrades to "no persisted state".
    static readStorage(storage, key) {
      try {
        return window[storage].getItem(key);
      } catch (error) {
        return null;
      }
    }

    static writeStorage(storage, key, value) {
      try {
        window[storage].setItem(key, value);
      } catch (error) {
        /* storage unavailable — badge simply re-appears next visit */
      }
    }

    static removeStorage(storage, key) {
      try {
        window[storage].removeItem(key);
      } catch (error) {
        /* nothing to clean up */
      }
    }

    connectedCallback() {
      this.toggleButton = this.querySelector('.td-notifications__toggle');
      this.panel = this.querySelector('.td-notifications__panel');
      this.badge = this.querySelector('.td-notifications__badge');
      this.closeButton = this.querySelector('[data-notifications-close]');
      this.form = this.querySelector('.td-notifications__form');
      this.sectionHeader = document.querySelector('.section-header');
      this.desktopQuery = window.matchMedia(TdNotifications.DESKTOP_QUERY);

      if (!this.toggleButton || !this.panel) return;

      this.signature = this.dataset.signature || '';
      this.itemCount = Number.parseInt(this.dataset.itemCount, 10) || 0;

      this.handleToggleClick = this.handleToggleClick.bind(this);
      this.handleCloseClick = this.handleCloseClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleOutsideClick = this.handleOutsideClick.bind(this);
      this.handleHeaderMutation = this.handleHeaderMutation.bind(this);
      this.handleResize = this.handleResize.bind(this);
      this.handleFormSubmit = this.handleFormSubmit.bind(this);
      this.handleEditorSelect = this.handleEditorSelect.bind(this);
      this.handleEditorDeselect = this.handleEditorDeselect.bind(this);

      this.toggleButton.addEventListener('click', this.handleToggleClick);
      if (this.closeButton) this.closeButton.addEventListener('click', this.handleCloseClick);
      if (this.form) this.form.addEventListener('submit', this.handleFormSubmit);
      document.addEventListener('keydown', this.handleKeydown);
      // `click` rather than `pointerdown`, matching how details-modal.js closes on an
      // outside click: the same event that opens the panel bubbles up here, so the
      // containment check always sees the toggle and can't race the open.
      document.addEventListener('click', this.handleOutsideClick);
      window.addEventListener('resize', this.handleResize);
      document.addEventListener('shopify:block:select', this.handleEditorSelect);
      document.addEventListener('shopify:block:deselect', this.handleEditorDeselect);

      // The sticky header hides itself on scroll-down by toggling a class. Observing
      // that class keeps the close-on-scroll behaviour without a scroll listener of
      // our own and without editing the vendor StickyHeader element.
      if (this.sectionHeader) {
        this.headerObserver = new MutationObserver(this.handleHeaderMutation);
        this.headerObserver.observe(this.sectionHeader, { attributeFilter: ['class'] });
      }

      this.syncUnreadState();
      this.restoreAfterSubscribe();
    }

    disconnectedCallback() {
      this.toggleButton?.removeEventListener('click', this.handleToggleClick);
      this.closeButton?.removeEventListener('click', this.handleCloseClick);
      this.form?.removeEventListener('submit', this.handleFormSubmit);
      document.removeEventListener('keydown', this.handleKeydown);
      document.removeEventListener('click', this.handleOutsideClick);
      window.removeEventListener('resize', this.handleResize);
      document.removeEventListener('shopify:block:select', this.handleEditorSelect);
      document.removeEventListener('shopify:block:deselect', this.handleEditorDeselect);
      this.headerObserver?.disconnect();
      document.body.classList.remove(TdNotifications.SCROLL_LOCK_CLASS, TdNotifications.BODY_OPEN_CLASS);
      window.clearTimeout(this.hideTimer);
      if (this.resizeFrame) cancelAnimationFrame(this.resizeFrame);
    }

    get isOpen() {
      return this.toggleButton.getAttribute('aria-expanded') === 'true';
    }

    /* Unread state */

    get hasUnread() {
      if (this.itemCount === 0) return false;
      return TdNotifications.readStorage('localStorage', TdNotifications.SEEN_STORAGE_KEY) !== this.signature;
    }

    syncUnreadState() {
      const unread = this.hasUnread;

      this.badge?.classList.toggle('td-notifications__badge--visible', unread);

      const label = unread ? this.dataset.unreadLabel : this.dataset.defaultLabel;

      if (label) this.toggleButton.setAttribute('aria-label', label);
    }

    // Persisted on open rather than on close, so the count is still recorded when the
    // panel is left open and the visitor navigates away via one of the item links.
    // The visual badge deliberately does not refresh here — see open().
    markAsSeen() {
      TdNotifications.writeStorage('localStorage', TdNotifications.SEEN_STORAGE_KEY, this.signature);
    }

    /* Open / close */

    // Transition length comes from CSS so there is a single source of truth — and it
    // reads as 0 under prefers-reduced-motion, which collapses the waits below.
    get transitionMs() {
      const raw = getComputedStyle(this.panel).transitionDuration.split(',')[0].trim();
      const value = Number.parseFloat(raw) || 0;

      return raw.endsWith('ms') ? value : value * 1000;
    }

    open() {
      window.clearTimeout(this.hideTimer);
      this.setPanelOffset();
      this.panel.hidden = false;
      document.body.classList.add(TdNotifications.SCROLL_LOCK_CLASS, TdNotifications.BODY_OPEN_CLASS);
      // Flush the display change before adding the class, otherwise both land in the
      // same style recalculation and the panel snaps open instead of transitioning.
      void this.panel.offsetHeight;
      this.panel.classList.add(TdNotifications.OPEN_CLASS);
      this.toggleButton.setAttribute('aria-expanded', 'true');
      // The design shows the badge still lit while the panel is open, so the count stays
      // visible until the panel closes even though it has already been marked seen.
      this.markAsSeen();
    }

    close({ returnFocus = false } = {}) {
      if (!this.isOpen) return;

      this.panel.classList.remove(TdNotifications.OPEN_CLASS);
      this.toggleButton.setAttribute('aria-expanded', 'false');
      this.syncUnreadState();
      document.body.classList.remove(TdNotifications.SCROLL_LOCK_CLASS);

      // [hidden] only goes back on once the panel has finished animating out; until then
      // the CSS keeps it non-interactive and delays `visibility`. The header stays raised
      // for the same stretch so the sheet is not overlapped on its way out.
      window.clearTimeout(this.hideTimer);
      this.hideTimer = window.setTimeout(() => {
        this.panel.hidden = true;
        document.body.classList.remove(TdNotifications.BODY_OPEN_CLASS);
      }, this.transitionMs);

      if (returnFocus) this.toggleButton.focus();
    }

    // The panel is position: fixed, so both offsets are measured live rather than
    // assumed: the header moves while sticky, and its right edge stops at
    // --page-width while the viewport keeps going.
    setPanelOffset() {
      if (this.sectionHeader) {
        const bottom = Math.max(this.sectionHeader.getBoundingClientRect().bottom, 0);
        this.style.setProperty('--td-notifications-panel-top', `${bottom}px`);
      }

      const anchor = this.closest('.header') || this.closest('.header__icons');

      if (anchor) {
        const viewportEnd = document.documentElement.clientWidth;
        const end = Math.max(viewportEnd - anchor.getBoundingClientRect().right, 0);
        this.style.setProperty('--td-notifications-panel-end', `${end}px`);
      }
    }

    /* Handlers */

    handleToggleClick() {
      if (this.isOpen) {
        this.close();
      } else {
        this.open();
      }
    }

    handleCloseClick() {
      this.close({ returnFocus: true });
    }

    handleKeydown(event) {
      if (event.key !== 'Escape' || !this.isOpen) return;

      this.close({ returnFocus: true });
    }

    handleOutsideClick(event) {
      if (!this.isOpen || this.contains(event.target)) return;

      this.close();
    }

    // Keeps the measured offsets honest while the panel is open — the header's height and
    // its right edge both move as the viewport crosses --page-width. rAF-throttled so a
    // drag-resize doesn't run the measurement on every event.
    handleResize() {
      if (!this.isOpen || this.resizeFrame) return;

      this.resizeFrame = requestAnimationFrame(() => {
        this.resizeFrame = null;
        if (this.isOpen) this.setPanelOffset();
      });
    }

    // Shopify's customer form posts and redirects, so the panel has to be reopened on the
    // way back. The flag keeps that specific to this form — form.posted_successfully? is
    // true for every customer form on the page, including the footer newsletter.
    handleFormSubmit() {
      TdNotifications.writeStorage('sessionStorage', TdNotifications.SUBMITTED_STORAGE_KEY, '1');
    }

    restoreAfterSubscribe() {
      const { SUBMITTED_STORAGE_KEY } = TdNotifications;

      if (TdNotifications.readStorage('sessionStorage', SUBMITTED_STORAGE_KEY) !== '1') return;

      TdNotifications.removeStorage('sessionStorage', SUBMITTED_STORAGE_KEY);
      this.open();
      this.querySelector('[data-notifications-message]')?.focus();
    }

    handleHeaderMutation() {
      if (!this.isOpen || !this.desktopQuery.matches) return;
      if (!this.sectionHeader.classList.contains(TdNotifications.HEADER_HIDDEN_CLASS)) return;

      this.close();
    }

    /* Theme editor */

    // The block events are document-wide, so each element claims only its own. `contains`
    // is true for the element itself, covering both the usual case (the target is the
    // selected block's <li> inside this element) and a selection targeting the element
    // directly. A mega-menu or progress-bar block in the same section is not ours.
    ownsEditorEvent(event) {
      return event.target instanceof Node && this.contains(event.target);
    }

    // Opens so the content a merchant is editing is visible without extra clicks.
    handleEditorSelect(event) {
      if (this.ownsEditorEvent(event)) this.open();
    }

    handleEditorDeselect(event) {
      if (this.ownsEditorEvent(event)) this.close();
    }
  }

  customElements.define('td-notifications', TdNotifications);
}
